'use strict';

/**
 * Broadcast when the buyer's selected supplier differs from the one this page is already displaying.
 *
 * Emitted by:
 *   - `supplier-state.js` after `reconcileSelectedSupplier()` writes a new hash to the cache
 *
 * Subscribed by:
 *   - `dealer_selection_modal.js` (skips its own `/ajax/dealers/selected` page-load fetch)
 *
 * Contract: the emitter MUST write `STORAGE_KEYS.SUPPLIER_V1` before triggering, so subscribers can
 * rely on the cache and the event agreeing. Only `supplier-state.js` may emit it.
 *
 * "Changed" is decided by diffing the server's reported hash against the cached hash rather than by
 * the server reporting a transition. That keeps the signal idempotent: duplicate, concurrent, and
 * out-of-order responses converge, because the second one sees a matching hash and stays silent.
 *
 * A response carrying no supplier is deliberately NOT an event and does NOT clear the cache. Only an
 * explicit action (`/ajax/dealers/clear`, manual clear) may unselect a supplier; treating an absent
 * supplier as "cleared" is what surfaced stale session reads as empty dealer names.
 *
 * @typedef {object} SupplierChangedPayload
 * @property {{ hash: string, name: string, interactiveGarageDealerCode: string }} supplier  Newly committed supplier.
 * @property {string|null} previousHash  Hash this page was displaying, or null on first resolution.
 *
 * Usage:
 *   const { SUPPLIER_CHANGED_EVENT } = require('public/js/lib/supplier-events');
 *   rp$(document).trigger(SUPPLIER_CHANGED_EVENT, [payload]);
 *   rp$(document).on(SUPPLIER_CHANGED_EVENT, (evt, payload) => { ... });
 */
const SUPPLIER_CHANGED_EVENT = 'supplier:changed';

/**
 * Broadcast every time the server reports a usable supplier, whether or not it differs from the
 * cached one.
 *
 * Emitted by:
 *   - `supplier-state.js` on every `reconcileSelectedSupplier()` call that receives a usable supplier
 *
 * Subscribed by:
 *   - `supplier-listing-refresh.js` (PLP rows)
 *   - `supplier-purchase-refresh.js` (legacy purchase module)
 *   - `supplier-offer-refresh.js` (product offer module)
 *
 * Exists because "the supplier changed" and "the rendered markup is stale" are different questions.
 * A page can render without a supplier while the buyer's cache already holds the one the server then
 * reports; `supplier:changed` stays silent on that match by design, so a refresh keyed to it would
 * never run and the page would stay stale across reloads. Subscribers therefore compare the reported
 * hash against the hash their markup was rendered for, which is self-correcting: it converges no
 * matter how the two got out of step, and costs nothing when they already agree.
 *
 * @typedef {object} SupplierResolvedPayload
 * @property {{ hash: string, name: string, interactiveGarageDealerCode: string }} supplier  Supplier the server reported.
 *
 * Usage:
 *   const { SUPPLIER_RESOLVED_EVENT } = require('public/js/lib/supplier-events');
 *   rp$(document).on(SUPPLIER_RESOLVED_EVENT, (evt, payload) => { ... });
 */
const SUPPLIER_RESOLVED_EVENT = 'supplier:resolved';

module.exports = { SUPPLIER_CHANGED_EVENT, SUPPLIER_RESOLVED_EVENT };
